import React, {useCallback, useContext, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Icon,
    IconButton,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {Add, AddComment, CheckCircle, Comment, RemoveCircle} from '@mui/icons-material';
import ZeiterfassungDashboardContext from '../../../../../../Contexts/ZeiterfassungDashboardContext/ZeiterfassungDashboardContext';
import ZeiterfassungDashboardNoteContext from '../../../../../../Contexts/ZeiterfassungDashboardContext/ZeiterfassungDashboardNoteContext';
import {ArbeitszeitService} from '../../../../../../services/openapi/services/ArbeitszeitService';
import APIWrapperContext from '../../../../../../services/APIWrapperContext';
import WorktimeDetails from './WorktimeDetails';
import {IWorktimeRowProps} from './types';

const WorktimeRow: React.FC<IWorktimeRowProps> = ({row, isAdmin, bauvorhabenList}) => {
    const [open, setOpen] = useState(false);
    const {preSetNote} = useContext(ZeiterfassungDashboardNoteContext);
    const {handle_Upadate_Arbeitszeiten_alle_MA} = useContext(ZeiterfassungDashboardContext);
    const {call_backend_api} = useContext(APIWrapperContext);
    const [showAddDialog, setShowAddDialog] = useState(false);

    const handleToggleOpen = useCallback(() => {
        setOpen((prev) => !prev);
    }, []);

    const handleNoteClick = useCallback(() => {
        preSetNote(
            row.note.length
                ? row.note[0]
                : {
                      id: undefined,
                      datum: row.DATUM,
                      notiz: '',
                      user_id: row.user_id,
                  }
        );
    }, [preSetNote, row.note, row.DATUM, row.user_id]);

    const handleAddArbeitszeit = useCallback(() => {
        setShowAddDialog(true);
    }, []);

    const handleCloseAddDialog = useCallback(() => {
        setShowAddDialog(false);
    }, []);

    const handleConfirmAddArbeitszeit = useCallback(() => {
        const defaultBauvorhaben =
            row.arbeitszeit_details.length > 0
                ? bauvorhabenList.find((bvh) => bvh.id === row.arbeitszeit_details[0].bvh)
                : bauvorhabenList[0];

        if (!defaultBauvorhaben) {
            console.error('No Bauvorhaben available');
            return;
        }

        const datumParts = row.DATUM.split('.');
        const datumFormatted = `${datumParts[2]}-${datumParts[1]}-${datumParts[0]}`;

        const beginTimestamp = `${datumFormatted}T08:00:00`;
        const endTimestamp = `${datumFormatted}T16:00:00`;

        const pauseInSeconds = Math.round(0.5 * 3600);
        const durationInSeconds = Math.round(7.5 * 3600);

        const newArbeitszeit = {
            datum: datumFormatted,
            beginn: beginTimestamp,
            ende: endTimestamp,
            pause: pauseInSeconds,
            dauer: durationInSeconds,
            bearbeiter: 'XX',
            kurzinfo: '',
            notiz: '',
            taetigkeit: '',
            bvh: {
                id: defaultBauvorhaben.id,
                bvh_name: defaultBauvorhaben.bvh_name,
                mandnr: defaultBauvorhaben.mandnr,
                projektnr: defaultBauvorhaben.projektnr,
                bvh_uuid: defaultBauvorhaben.bvh_uuid,
            },
        };

        call_backend_api(
            ArbeitszeitService.addWorktimeAdminArbeitzeitArbeitszeiteintragAdminPost(row.user_id, newArbeitszeit),
            () => {
                handle_Upadate_Arbeitszeiten_alle_MA();
                setShowAddDialog(false);
            },
            undefined,
            undefined,
            true,
            'Arbeitszeiteintrag erfolgreich hinzugefügt',
            'Fehler beim Hinzufügen des Arbeitszeiteintrags'
        );
    }, [row.DATUM, row.user_id, row.arbeitszeit_details, bauvorhabenList, call_backend_api, handle_Upadate_Arbeitszeiten_alle_MA]);

    const noteTooltip = useMemo(
        () => String(row.note.length ? row.note.map((el) => el.notiz).join('') : 'Neue Notiz'),
        [row.note]
    );

    return (
        <React.Fragment>
            <TableRow
                sx={{
                    '& > *': {borderBottom: 'unset'},
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    transition: 'background-color 0.2s',
                    '&:nth-of-type(odd)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    },
                }}>
                <TableCell>
                    {row.arbeitszeit_details.length > 0 && (
                        <IconButton
                            aria-label='expand row'
                            size='small'
                            onClick={handleToggleOpen}
                            sx={{
                                transition: 'transform 0.2s',
                                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                                color: 'primary.main',
                                '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                },
                            }}>
                            <KeyboardArrowDownIcon />
                        </IconButton>
                    )}
                </TableCell>
                <TableCell align='left'>
                    <Typography variant='body2' sx={{fontWeight: 500}}>
                        {row.BEARBEITER}
                    </Typography>
                </TableCell>
                <TableCell align='right'>
                    <Typography variant='body2' fontWeight={500}>
                        {row.gesamt_arbeitszeit.toFixed(2)}
                    </Typography>
                </TableCell>
                <TableCell align='right'>
                    <Typography variant='body2'>{row.gesamt_pause.toFixed(2)}</Typography>
                </TableCell>
                <TableCell
                    align='right'
                    sx={{
                        color: row.arbeitszeit_erfuellt ? 'success.main' : 'error.main',
                    }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 0,
                            flexWrap: 'nowrap',
                        }}>
                        {row.arbeitszeit_details.length > 0 ? (
                            <>
                                <Typography variant='body2' noWrap fontWeight={500}>
                                    {row.arbeitszeit_erfuellt
                                        ? row.gesamt_arbeitszeit > 8.5
                                            ? 'AZ überschritten'
                                            : ''
                                        : 'AZ unterschritten'}
                                </Typography>
                                <Icon sx={{fontSize: 18, flexShrink: 0, height: 25, width: 25}}>
                                    {row.arbeitszeit_erfuellt ? <CheckCircle /> : <RemoveCircle />}
                                </Icon>
                            </>
                        ) : (
                            <>
                                <Typography variant='body2' noWrap fontWeight={500}>
                                    Kein Eintrag
                                </Typography>
                                <Icon sx={{fontSize: 16, flexShrink: 0, height: 25, width: 25}}>
                                    <RemoveCircle />
                                </Icon>
                            </>
                        )}
                    </Box>
                </TableCell>
                <TableCell align='right'>
                    <Box sx={{display: 'flex', gap: 0.5, justifyContent: 'flex-end'}}>
                        <IconButton
                            size='small'
                            sx={{
                                color: row.note.length ? 'primary.main' : 'text.disabled',
                                '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                },
                            }}
                            onClick={handleNoteClick}
                            title={noteTooltip}>
                            {row.note.length ? <Comment /> : <AddComment />}
                        </IconButton>
                        {isAdmin && (
                            <IconButton
                                size='small'
                                sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    width: 32,
                                    height: 32,
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                        color: 'primary.main',
                                    },
                                }}
                                onClick={handleAddArbeitszeit}
                                title='Neuen Arbeitszeiteintrag hinzufügen'>
                                <Add sx={{fontSize: 18}} />
                            </IconButton>
                        )}
                    </Box>
                    <Dialog open={showAddDialog} onClose={handleCloseAddDialog}>
                        <DialogTitle>Arbeitszeiteintrag hinzufügen</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Möchten Sie einen neuen Arbeitszeiteintrag für {row.BEARBEITER} am {row.DATUM} erstellen?
                                <br />
                                <br />
                                Der Eintrag wird mit Standardwerten erstellt (08:00 - 16:00, 0.5h Pause). Sie können diese nach
                                dem Erstellen bearbeiten.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseAddDialog} color='inherit'>
                                Abbrechen
                            </Button>
                            <Button onClick={handleConfirmAddArbeitszeit} color='primary' variant='contained'>
                                Erstellen
                            </Button>
                        </DialogActions>
                    </Dialog>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={6}>
                    <Collapse in={open} timeout='auto' unmountOnExit>
                        {open && <WorktimeDetails details={row.arbeitszeit_details} isAdmin={isAdmin} bauvorhabenList={bauvorhabenList} />}
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

export default React.memo(WorktimeRow);
